import { TableRow } from "@mui/material";
import { useContext, useState } from "react";

import { ProductCustomer } from "../../../../../data/entities";
import { ROLE } from "../../../../../data/enums";
import { AuthenticationContext } from "../../../../../provider/AuthenticationProvider";
import CustomTable from "../../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../../shared/components/IconButton";
import TextBox from "../../../../../shared/components/TextBox";
import { StyledNavLink } from "../../../../../shared/styled/TableStyles";
import DeleteIcon from "../../../../../shared/svg/DeleteIcon";
import {
    formatInternationalNumber,
    parseInternationalToNumber,
} from "../../../../../utils/format";
import { error } from "../../../../../utils/toast";
import { ProductCustomerColumns } from "../../../../../configs/products";
import { StyledButton } from "../../../../MaterialOrder/Styled";
import {
    ContentWrapper,
    ProductButtonRow,
    ProductTableText,
} from "../../../../ProductOrder/single/Styled";
import { AddProductCustomerModal } from "./AddCustomerModal";

interface ProductCustomerRowProps {
    item: ProductCustomer;
    extraRowProps: {
        canEdit: boolean;
        removeItem: (item: ProductCustomer) => void;
        updateCustomerPrice: (item: ProductCustomer, price: number) => void;
    };
}

export const ProductCustomerRow = ({ item, extraRowProps }: ProductCustomerRowProps) => (
    <TableRow>
        <StyledTableCell align="left">
            <StyledNavLink to={`/customers/${item.customer.id}`}>
                {item.customer.name}
            </StyledNavLink>
        </StyledTableCell>
        <StyledTableCell align="left">
            <TextBox
                numeric
                text={formatInternationalNumber(item.price ?? "")}
                onChangeText={(value: string) => {
                    const price = parseInternationalToNumber(value, error);
                    if (Number.isFinite(price)) {
                        extraRowProps.updateCustomerPrice(item, price);
                    }
                }}
                disabled={!extraRowProps.canEdit}
            />
        </StyledTableCell>
        <StyledTableCell align="left">
            <IconButton
                onClick={() => extraRowProps.removeItem(item)}
                disabled={!extraRowProps.canEdit}
            >
                <DeleteIcon />
            </IconButton>
        </StyledTableCell>
    </TableRow>
);

interface ProductCustomerProps {
    customers: ProductCustomer[];
    defaultPrice: number;
    mergeProduct: Function;
    isView?: boolean;
}

export const ProductCustomerTab = ({
    customers,
    defaultPrice,
    mergeProduct,
    isView,
}: ProductCustomerProps) => {
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canEdit = !isView && [
        ROLE.MANAGER,
        ROLE.SALE_MANAGER,
        ROLE.SALE_STAFF,
    ].includes(role);

    const removeCustomer = (target: ProductCustomer) => {
        mergeProduct({
            customers: customers.filter(
                (item) => item.customer.id !== target.customer.id,
            ),
        });
    };

    const updateCustomerPrice = (target: ProductCustomer, price: number) => {
        mergeProduct({
            customers: customers.map((item) =>
                item.customer.id === target.customer.id
                    ? { ...item, price }
                    : item,
            ),
        });
    };

    return (
        <>
            {isCustomerModalOpen && canEdit && (
                <AddProductCustomerModal
                    selectedCustomers={customers}
                    defaultPrice={defaultPrice}
                    open={isCustomerModalOpen}
                    handleClose={() => setIsCustomerModalOpen(false)}
                    mergeProduct={mergeProduct}
                />
            )}

            <ContentWrapper>
                <ProductTableText>Danh sách khách hàng</ProductTableText>
                <CustomTable
                    itemList={customers}
                    RowElement={ProductCustomerRow}
                    TableColumns={ProductCustomerColumns}
                    extraRowProps={{
                        removeItem: removeCustomer,
                        updateCustomerPrice,
                        canEdit,
                    }}
                />
                {customers.length === 0 && "Không có khách hàng được chọn"}
                {canEdit && (
                    <ProductButtonRow>
                        <StyledButton
                            variant="contained"
                            onClick={() => setIsCustomerModalOpen(true)}
                        >
                            Thêm khách hàng
                        </StyledButton>
                    </ProductButtonRow>
                )}
            </ContentWrapper>
        </>
    );
};
